import React from 'react'

import { SearchContextState } from '@elastic/react-search-ui/lib/cjs/withSearch'
import { ApiProxyConnector } from '@elastic/search-ui-elasticsearch-connector'
import { useQueryClient } from 'react-query'

import useNextRouting from 'app/hooks/useNextRouting'
import { vehicleKeys } from 'app/services/Query/vehicle/vehicle.keys'
import useAppStore from 'app/store/app.store'
import useAuthStore from 'app/store/auth.store'
import { IS_NATIVE_APP, WEBSITE_URL } from 'app/utils/constants/env.constants'
import { USER_ROLE } from 'app/utils/constants/role.constants'
import {
  getFormattedSearchFilters,
  getVehicleUniversalFilters
} from 'app/utils/helpers/elastic.helpers'
import { getSearchCategoryId } from 'app/utils/helpers/listings.helpers'

import ElasticSearchProvider from './ElasticSearchProvider'

export type ElasticSearchProviderProps = React.ComponentProps<
  typeof ElasticSearchProvider
>

const facetCityLimit = 250
const facetCountryLimit = 50

const connector = new ApiProxyConnector({
  basePath: `${WEBSITE_URL}/api/elastic`,
  ...(IS_NATIVE_APP && {
    fetchOptions: {
      headers: { secret: process.env.EXPO_INTERNAL_API_SECRET_TOKEN ?? '' }
    }
  })
})

export const FACETS = {
  ['part_number.keyword']: { type: 'value' },
  ['country.keyword']: { type: 'value', size: facetCountryLimit },
  ['full_country_name.keyword']: { type: 'value', size: facetCountryLimit },
  ['city.keyword']: { type: 'value', size: facetCityLimit }
}

export const DISJUNCTIVE_FACETS = [
  'part_number.keyword',
  'country.keyword',
  'full_country_name.keyword',
  'city.keyword'
]

export const SEARCH_FIELDS = {
  title: { weight: 5 },
  makes: { weight: 3 },
  models: { weight: 4 },
  part_number: { weight: 4 },
  description: { weight: 1 },
  series: { weight: 1 },
  trims: { weight: 1 },
  generations: { weight: 1 },
  category: { weight: 4 },
  contact_name: { weight: 4 }
}

export const RESULT_FIELDS = {
  makes: { raw: {} },
  title: { raw: {} },
  id: { raw: {} },
  city: { raw: {} },
  part_number: { raw: {} },
  usd_price: { raw: {} },
  eur_price: { raw: {} },
  aud_price: { raw: {} },
  gbp_price: { raw: {} },
  jpy_price: { raw: {} },
  attachment_url: { raw: {} },
  slug: { raw: {} },
  seller_id: { raw: {} },
  auction_close_date: { raw: {} },
  auction_reserve_status: { raw: {} },
  status: { raw: {} },
  international_shipping: { raw: {} },
  usd_trade_price: { raw: {} },
  eur_trade_price: { raw: {} },
  aud_trade_price: { raw: {} },
  gbp_trade_price: { raw: {} },
  jpy_trade_price: { raw: {} },
  trade_pricing: { raw: {} },
  subtitle: { raw: {} },
  contact_name: { raw: {} }
}

type SearchProviderProps = Omit<ElasticSearchProviderProps, 'config'> & {
  initialState?: ElasticSearchProviderProps['config']['initialState']
  searchQuery?: ElasticSearchProviderProps['config']['searchQuery']
  searchCategory?: string
}

const SearchProvider = ({
  initialState,
  searchQuery,
  searchCategory,
  children
}: SearchProviderProps) => {
  const queryClient = useQueryClient()

  const configurationOptions: ElasticSearchProviderProps['config'] = {
    apiConnector: connector,
    initialState: {
      resultsPerPage: 20,
      ...initialState
    },
    onSearch: (state, config, next) => {
      const { filters = [], sortField, ...restState } = state || {}
      const { currency } = useAppStore.getState()
      const newFilters = [...filters]

      const { user } = useAuthStore.getState()
      const isBusinessUser = user?.role === USER_ROLE.BUSINESS

      // update price after change currency
      const priceIndex = filters?.findIndex(({ field }) => field === 'price')
      const isPriceSort = sortField === 'price'
      const fieldPrice = isBusinessUser
        ? `${currency}_trade_price`
        : `${currency}_price`

      if (priceIndex !== -1) {
        const priceFilter = newFilters[priceIndex]

        newFilters.splice(priceIndex, 1, {
          type: priceFilter?.type || 'any',
          values: priceFilter?.values || [],
          field: fieldPrice
        })
      }

      // setup category if category page
      const categoryId = getSearchCategoryId({ searchCategory, queryClient })

      if (!!categoryId) {
        newFilters.push({
          type: 'any',
          values: [categoryId],
          field: 'category_id.keyword'
        })
      }

      const universalParams =
        queryClient.getQueryData<VehicleUniversalParamsModel>(
          vehicleKeys.getVehicleUniversalParams()
        )

      // get vehicle filters with universal option searching
      const vehicleFilters = getVehicleUniversalFilters(
        newFilters,
        universalParams
      )

      const isVehicleFilters = !!vehicleFilters && vehicleFilters?.length > 0
      const formattedFilters = isVehicleFilters
        ? getFormattedSearchFilters(newFilters, vehicleFilters)
        : newFilters

      const hasFilters = formattedFilters?.length > 0 || !!restState?.searchTerm

      const newState = {
        ...restState,
        sortField: isPriceSort ? fieldPrice : sortField,
        filters: formattedFilters,
        ...(!sortField && {
          sortField: hasFilters ? '' : 'published_at',
          sortDirection: hasFilters
            ? ''
            : ('desc' as SearchContextState['sortDirection'])
        })
      }

      return next(newState, config)
    },
    searchQuery,
    trackUrlState: IS_NATIVE_APP ? false : true,
    alwaysSearchOnInitialLoad: true
  }

  const config = useNextRouting(configurationOptions, 'search')

  return (
    <ElasticSearchProvider config={config}>{children}</ElasticSearchProvider>
  )
}

export default SearchProvider
